import type { Herkunft, Route, SupplierAreaFilter } from "../../router.js";
import type { AkteSprung, AktenTabId } from "../../views/KundenAkte.js";

/**
 * Der OBJEKTBEZUG einer Sprungstelle („Öffnen statt Springen“, Spec F1–F16): welches Objekt die Stelle nennt — als Daten,
 * aus denen der Gastgeber das Panel öffnet (`oeffne`) und `sprungZiel` die Route bildet (`springe`). Die Stellen kennen
 * damit weder Route noch Panel; Rechte und Stufen entscheidet der Gastgeber (`kannOeffnen`, `kannSpringen`).
 */

/** Die Arten mit Objektpanel im Gastgeber (`INHALTE`); Angebot, Auftrag und Rechnung ab Stufe 3. */
export const PANEL_ARTEN = ["ticket", "meilenstein", "liefervertrag", "eingangsrechnung", "angebot", "auftrag", "rechnung"] as const;
export type PanelArt = (typeof PANEL_ARTEN)[number];
/** Die Arten, die als Vorgang in einem Reiter der Akte stehen. */
export type AkteObjektArt = Exclude<PanelArt, "ticket" | "meilenstein">;

export type ObjektBezug =
  /** `label` wahlfrei (PF20): Glocke, Cockpit, Akte-Tickets kennen nur die Kennung; der Ticketdialog lädt seinen Titel. */
  | { art: "ticket"; id: string; label?: string }
  | { art: "meilenstein"; id: string; label: string; companyId: string | null }
  | { art: AkteObjektArt; id: string; customerId: string; companyId: string; label: string }
  /** Nur Sprung: ein Reiter der Akte, wahlweise mit Vorgang (Notiz, Wichtiges, Vorgang ohne Unternehmen) oder Ordner
   *  (`null` = Wurzel) — PF4. */
  | { art: "akte"; id: string; reiter: AktenTabId; companyId?: string; vorgangId?: string; folderId?: string | null; label: string }
  /** Objekte mit eigenem Panel ihres Bereichs (Liquidität, F15, PF5): nie Gastgeber, nie Sprung. */
  | { art: "konto" | "planposition"; id: string; label: string };

/** Was einen Panelknopf haben kann: alles außer dem reinen Sprung in die Akte. */
export type PanelBezug = Exclude<ObjektBezug, { art: "akte" }>;
/** Was der Gastgeber öffnet. */
export type GastBezug = Extract<ObjektBezug, { art: PanelArt }>;
/** Die Arten ohne Ort zum Springen (keine Route) — eine Liste für Typ und Prüfung. */
const OHNE_ORT = ["meilenstein", "konto", "planposition"] as const;
/** Was einen Ort zum Springen hat. */
export type SprungBezug = Exclude<ObjektBezug, { art: (typeof OHNE_ORT)[number] }>;
/** Was in der Akte steht: ein Vorgang oder ein Reiter (`bezugAusAkteZiel`). */
export type AkteZielBezug = Exclude<SprungBezug, { art: "ticket" }>;

/** Der Reiter der Akte je Vorgangsart. */
export const AKTE_REITER: Readonly<Record<AkteObjektArt, AktenTabId>> = {
  liefervertrag: "liefervertraege", eingangsrechnung: "eingangsrechnungen", angebot: "angebote", auftrag: "auftraege",
  rechnung: "rechnungen",
};

export function istGastBezug(b: ObjektBezug): b is GastBezug {
  return (PANEL_ARTEN as readonly string[]).includes(b.art);
}

export function istSprungBezug(b: ObjektBezug): b is SprungBezug {
  return !(OHNE_ORT as readonly string[]).includes(b.art);
}

/** Dasselbe Objekt (Art und Kennung)? */
export function gleicherBezug(a: ObjektBezug, b: ObjektBezug): boolean {
  return a.art === b.art && a.id === b.id;
}

/** Woher ein Sprung kommt: Quelle für den Satz „Aus … geöffnet.“, Herkunft und Status-Chips für den Rückweg. */
export type SprungQuelle = { source: AkteSprung["source"]; herkunft?: Herkunft; originFilter?: SupplierAreaFilter };

type QuelleJe = { [N in Route["name"]]: (r: Extract<Route, { name: N }>) => SprungQuelle };

const AUS_DER_SUCHE = (r: { name: Herkunft }): SprungQuelle => ({ source: "search", herkunft: r.name });
const AUS_DEM_EINKAUF = (r: { name: "liefervertraege" | "eingangsrechnungen"; filter?: SupplierAreaFilter }): SprungQuelle =>
  ({ ...AUS_DER_SUCHE(r), ...(r.filter === undefined || r.filter.length === 0 ? {} : { originFilter: r.filter }) });
const UEBERSICHT = (): SprungQuelle => ({ source: "overview" });

/** Die Tabelle über `Route["name"]` (PF7) — eine neue Route ohne Zeile bricht den Bau ab. */
const QUELLE: QuelleJe = {
  liquidity: () => ({ source: "liquidity", herkunft: "liquidity" }),
  cockpit: () => ({ source: "cockpit", herkunft: "cockpit" }),
  // Mit offenem Ticketdialog kommt der Sprung aus einem Ticket (Bezug, Kunde; A5); ohne ihn aus den Terminen der Liste,
  // Zeitachse bzw. des Kalenders (T20, A6) — „Aus den Terminen geöffnet.“
  tickets: (r) => ({ source: r.id === undefined ? "calendar" : "ticket" }),
  kalender: (r) => ({ source: r.ticketId === undefined ? "calendar" : "ticket" }),
  liefervertraege: AUS_DEM_EINKAUF,
  eingangsrechnungen: AUS_DEM_EINKAUF,
  angebote: AUS_DER_SUCHE,
  auftraege: AUS_DER_SUCHE,
  rechnungen: AUS_DER_SUCHE,
  kunde: (r) => ({ source: r.tab === "dateien" ? "files" : "overview" }),
  kundenliste: UEBERSICHT, kundeNeu: UEBERSICHT, einstellungen: UEBERSICHT, artikel: UEBERSICHT,
  schnittstellen: UEBERSICHT, verwaltung: UEBERSICHT, unbekannt: UEBERSICHT,
};

/** Quelle, Herkunft und Status-Chips eines Sprungs von der Route `von` aus (Spec F10, PF7). */
export function sprungQuelle(von: Route): SprungQuelle {
  return (QUELLE[von.name] as (r: Route) => SprungQuelle)(von);
}

/**
 * DIE EINE Routenbildung für Sprünge (Spec F10): Ticket → `/tickets/<id>`; Vorgang → Akte des Kunden, Reiter seiner Art,
 * Vorgang, Unternehmen; Akte → Reiter samt Vorgang bzw. Ordner. Quelle und Herkunft nach `sprungQuelle`; bleibt der Sprung
 * in der offenen Akte, reisen deren Herkunft und Status-Chips mit (heute `akteHerkunft` in App.tsx).
 */
export function sprungZiel(b: SprungBezug, von: Route): Route {
  if (b.art === "ticket") return { name: "tickets", id: b.id };
  const kunde = b.art === "akte" ? b.id : b.customerId;
  const q = sprungQuelle(von);
  const mitAkte = von.name === "kunde" && von.id === kunde
    ? { ...(von.herkunft === undefined ? {} : { herkunft: von.herkunft }),
        ...(von.originFilter === undefined ? {} : { originFilter: von.originFilter }) }
    : {};
  const quelle = { source: q.source, ...(q.herkunft === undefined ? {} : { herkunft: q.herkunft }),
    ...(q.originFilter === undefined ? {} : { originFilter: q.originFilter }), ...mitAkte };
  if (b.art === "akte") {
    return { name: "kunde", id: b.id, tab: b.reiter, ...(b.companyId === undefined ? {} : { companyId: b.companyId }),
      ...(b.vorgangId === undefined ? {} : { vorgangId: b.vorgangId }),
      ...(b.folderId === undefined ? {} : { folderId: b.folderId }), ...quelle };
  }
  return { name: "kunde", id: b.customerId, tab: AKTE_REITER[b.art], vorgangId: b.id, companyId: b.companyId, ...quelle };
}

const ART_DES_REITERS = new Map<AktenTabId, AkteObjektArt>(
  (Object.entries(AKTE_REITER) as [AkteObjektArt, AktenTabId][]).map(([art, reiter]) => [reiter, art]));

/**
 * Ein Ziel in der Akte (Zeile der Kundenübersicht, Termin) als Bezug (PF4): eine Objektart nur mit Objekt, Reiter einer
 * Vorgangsart und Unternehmen; sonst der Akte-Bezug mit Vorgang bzw. Ordner — er bleibt ein Sprung wie heute.
 */
export function bezugAusAkteZiel(z: { customerId: string; tab: AktenTabId; objectId?: string | null; companyId?: string;
  folderId?: string | null }, label: string): AkteZielBezug {
  const art = ART_DES_REITERS.get(z.tab);
  if (art !== undefined && typeof z.objectId === "string" && z.companyId !== undefined) {
    return { art, id: z.objectId, customerId: z.customerId, companyId: z.companyId, label };
  }
  return { art: "akte", id: z.customerId, reiter: z.tab, ...(z.companyId === undefined ? {} : { companyId: z.companyId }),
    ...(typeof z.objectId === "string" ? { vorgangId: z.objectId } : {}),
    ...(z.folderId === undefined ? {} : { folderId: z.folderId }), label };
}
